package fyi.moko.hotpl.ui.util

import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceStatus
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/** A place whose stored visit lands on one of today's recall milestones. */
data class RecallCandidate(
    val placeId: String,
    val placeName: String,
    val daysAgo: Int,
    val line: String,
)

/**
 * Glow fraction threshold above which a place is considered "over-visited" and is
 * excluded from today's recall. Tied to [visitGlowFraction] so that 9+ visits
 * (glow ≈ 0.889) drop off, keeping the recall cue for places that still feel
 * occasional or special.
 */
const val OVER_VISITED_GLOW_THRESHOLD = 0.8f

/**
 * Turns the stored visits into "today's recall" for the app-open cue (PROJECT.md
 * §3.4): liked places only, one line per place (the oldest matching memory — the
 * deepest — wins), ordered most-striking first (older = more striking). Milestones
 * are the multi-interval set in [recallLine] (49d / 100d / yearly). Pure and
 * deterministic: same data + [now] → same list, and an empty list means the home
 * shows nothing (no empty banner).
 *
 * [visitsByPlace] maps a place id to its visit timestamps (epoch millis); callers
 * assemble it from the repository. Kept out of the ViewModel so the selection rule
 * is judged in isolation before any UI depends on it.
 */
fun todaysRecalls(
    places: List<Place>,
    visitsByPlace: Map<String, List<Long>>,
    now: Long,
    zone: TimeZone = TimeZone.currentSystemDefault(),
): List<RecallCandidate> {
    val today = Instant.fromEpochMilliseconds(now).toLocalDateTime(zone).date
    fun daysAgo(epochMillis: Long): Int =
        Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(zone).date.daysUntil(today)

    return places
        .filter { place ->
            val visits = visitsByPlace[place.id].orEmpty()
            val glow = visitGlowFraction(visits.size)
            place.liked && !place.isDeleted && place.status == PlaceStatus.VISITED && glow < OVER_VISITED_GLOW_THRESHOLD
        }
        .mapNotNull { place ->
            // recallLine and daysAgo both convert epoch → local date; compute each once per visit.
            visitsByPlace[place.id]
                .orEmpty()
                .mapNotNull { visitedAt ->
                    recallLine(visitedAt, now, zone)?.let { line ->
                        RecallCandidate(
                            placeId = place.id,
                            placeName = place.name,
                            daysAgo = daysAgo(visitedAt),
                            line = line,
                        )
                    }
                }
                .maxByOrNull { it.daysAgo }
        }
        .sortedWith(compareByDescending<RecallCandidate> { it.daysAgo }.thenBy { it.placeName })
}
